import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, ArrowUpRight, Compass, Layers, Users } from "lucide-react";
import { Reveal, MaskedLines, Overline, Chapter } from "../components/Reveal";
import { unsplashSrc, unsplashSrcset } from "../lib/api";
import { BuyerForm, PartnerForm } from "../components/EnquiryForms";

const IMG = {
    hero: "https://images.unsplash.com/photo-1738844153732-a485f0e78382?q=85&w=2400&auto=format&fit=crop",
    india: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?q=80&w=1400&auto=format&fit=crop",
    dubai: "https://images.unsplash.com/flagged/photo-1559717201-fbb671ff56b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxkdWJhaSUyMGx1eHVyeSUyMHNreWxpbmUlMjBuaWdodHxlbnwwfHx8fDE3ODgyMDY4OTN8MA&ixlib=rb-4.1.0&q=85",
    interior: "https://images.unsplash.com/photo-1720247520862-7e4b14176fa8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBpbnRlcmlvciUyMGRlc2lnbiUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg4MjA2ODkzfDA&ixlib=rb-4.1.0&q=85",
    interiorAlt: "https://images.unsplash.com/photo-1646987916641-1f3c8992daa2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBpbnRlcmlvciUyMGRlc2lnbiUyMGxpdmluZyUyMHJvb218ZW58MHx8fHwxNzg4MjA2ODkzfDA&ixlib=rb-4.1.0&q=85",
};

const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
    const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

    return (
        <section ref={ref} data-testid="hero-section" className="relative flex min-h-[100svh] items-end overflow-hidden bg-charcoal">
            <motion.div className="absolute inset-0" style={{ y, scale }}>
                <img
                    src={unsplashSrc(IMG.hero, 1800, 82)}
                    srcSet={`${unsplashSrc(IMG.hero, 1200, 82)} 1200w, ${unsplashSrc(IMG.hero, 1800, 82)} 1800w, ${unsplashSrc(IMG.hero, 2400, 82)} 2400w`}
                    sizes="100vw"
                    alt="Contemporary architectural composition"
                    className="h-full w-full object-cover"
                    fetchpriority="high"
                    decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/40 to-charcoal/20" />
            </motion.div>
            <div className="relative z-10 mx-auto w-full max-w-[1400px] px-6 pb-24 pt-40 md:px-12 md:pb-32">
                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.1, duration: 1 }}
                    className="text-[11px] font-medium uppercase tracking-[0.35em] text-gold drop-shadow-[0_1px_10px_rgba(0,0,0,0.7)]"
                    data-testid="hero-overline"
                >
                    Real Estate Investments &amp; Partnerships
                </motion.p>
                <h1 className="mt-8 font-serif text-[13vw] font-light leading-[0.98] tracking-tight text-ivory sm:text-6xl md:text-7xl lg:text-[5.5rem]">
                    <MaskedLines
                        lines={["Real Estate.", "Selected With", "Perspective."]}
                        accentLast
                    />
                </h1>
                <motion.p
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.9, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/85 drop-shadow-[0_1px_10px_rgba(0,0,0,0.6)] md:text-lg"
                    data-testid="hero-subcopy"
                >
                    LUXATI helps buyers and investors discover, evaluate and connect with carefully
                    selected real estate opportunities across India and Dubai.
                </motion.p>
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.1, duration: 1, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-12 flex flex-wrap items-center gap-4"
                >
                    <Link
                        to="/opportunities"
                        data-testid="hero-explore-cta"
                        className="group inline-flex items-center gap-3 bg-ivory px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:bg-gold"
                    >
                        Explore Opportunities
                        <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                    </Link>
                    <Link
                        to="/contact"
                        data-testid="hero-speak-cta"
                        className="group inline-flex items-center gap-3 border border-ivory/40 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:border-gold hover:text-gold"
                    >
                        Speak With LUXATI
                        <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </motion.div>
            </div>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1.6, duration: 1 }}
                className="absolute bottom-8 right-6 hidden items-center gap-3 md:right-12 md:flex"
            >
                <span className="text-[10px] uppercase tracking-[0.3em] text-ivory/50">Scroll</span>
                <motion.span
                    className="block h-10 w-px bg-ivory/40"
                    animate={{ scaleY: [1, 0.4, 1] }}
                    transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
                    style={{ originY: 0 }}
                />
            </motion.div>
        </section>
    );
};

const EditorialMarquee = () => (
    <div data-testid="editorial-marquee" className="border-y border-ink/10 bg-ivory py-7">
        <Marquee speed={28} gradient={false} pauseOnHover>
            {["Understand", "Discover", "Evaluate", "Connect"].map((w) => (
                <span key={w} className="mx-14 flex items-center gap-14">
                    <span className="font-serif text-3xl font-light italic tracking-tight text-ink/70 md:text-4xl">{w}</span>
                    <span className="h-1.5 w-1.5 rotate-45 bg-gold/70" />
                </span>
            ))}
        </Marquee>
    </div>
);

const WHAT_WE_DO = [
    {
        icon: Compass,
        title: "Selected Opportunities",
        copy: "We identify residential and commercial projects worth considering — instead of presenting everything on the market.",
    },
    {
        icon: Layers,
        title: "Context Before Commitment",
        copy: "Location, pricing, developer record and project fundamentals are examined before an opportunity reaches you.",
    },
    {
        icon: Users,
        title: "A Working Network",
        copy: "Direct relationships with developers, sales teams and real-estate partners give buyers a clearer path from evaluation to purchase.",
    },
];

const STEPS = [
    { n: "01", t: "Understand", c: "We begin with your objectives, budget, location preference and timeline." },
    { n: "02", t: "Discover", c: "Relevant opportunities are identified and shortlisted — no endless listings." },
    { n: "03", t: "Evaluate", c: "Consider project, location, pricing and market context before deciding." },
    { n: "04", t: "Connect", c: "When you're ready, we connect you with the relevant developer, sales team or real estate partner." },
];

const PRINCIPLES = [
    { t: "Curated", c: "A shortlist you can actually evaluate — not a catalogue." },
    { t: "Informed", c: "The project, the location and the commercial context, before you commit." },
    { t: "Connected", c: "Buyers, developers and partners working from the same information." },
];

const Home = () => (
    <main data-testid="home-page">
        <Hero />
        <EditorialMarquee />

        {/* 01 — Introduction */}
        <section data-testid="intro-section" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
            <Chapter number="01" label="Introduction" />
            <div className="mt-12 grid gap-12 md:grid-cols-12">
                <Reveal className="md:col-span-5">
                    <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                        Beyond Property <span className="italic text-gold">Search.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.15} className="md:col-span-6 md:col-start-7">
                    <p className="text-base font-light leading-relaxed text-ink/70 md:text-lg">
                        Real estate decisions require more than listings. They require context.
                    </p>
                    <p className="mt-6 text-base font-light leading-relaxed text-ink/70 md:text-lg">
                        LUXATI identifies a small number of opportunities worth your attention — and
                        gives you the context to judge them properly.
                    </p>
                </Reveal>
            </div>
        </section>

        {/* 02 — What We Do */}
        <section data-testid="what-we-do-section" className="border-t border-ink/10 bg-white/50">
            <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-36">
                <Chapter number="02" label="What We Do" />
                <div className="mt-16 grid gap-px bg-ink/10 md:grid-cols-3">
                    {WHAT_WE_DO.map((item, i) => (
                        <Reveal key={item.title} delay={i * 0.12} className="group bg-ivory p-10 transition-colors duration-700 hover:bg-white md:p-12">
                            <item.icon className="h-5 w-5 text-gold" strokeWidth={1.25} />
                            <p className="mt-10 font-serif text-sm italic text-ink/40">{String(i + 1).padStart(2, "0")}</p>
                            <h3 className="mt-3 font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">
                                {item.title}
                            </h3>
                            <span className="mt-5 block h-px w-8 bg-gold transition-all duration-700 group-hover:w-16" />
                            <p className="mt-5 text-[15px] font-light leading-relaxed text-ink/70">{item.copy}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* 03 — Markets */}
        <section data-testid="markets-section" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
            <Chapter number="03" label="Markets" />
            <Reveal className="mt-12 max-w-2xl">
                <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl">
                    Where We <span className="italic text-gold">Focus.</span>
                </h2>
            </Reveal>
            <div className="mt-16 grid gap-8 md:grid-cols-12">
                <Reveal className="md:col-span-7">
                    <Link to="/opportunities?market=india" data-testid="market-card-india" className="group relative block">
                        <div className="img-frame aspect-[4/3] bg-secondary">
                            <img
                                src={unsplashSrc(IMG.india, 1200)}
                                srcSet={unsplashSrcset(IMG.india)}
                                sizes="(max-width: 768px) 100vw, 58vw"
                                alt="Premium residential architecture in India"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent" />
                        <span className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center border border-ivory/50 text-ivory opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                            <ArrowUpRight className="h-4 w-4" />
                        </span>
                        <div className="absolute bottom-0 left-0 p-8 md:p-10">
                            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">Primary Market</p>
                            <h3 className="mt-2 font-serif text-3xl font-light tracking-tight text-ivory md:text-4xl">India</h3>
                            <p className="mt-2 max-w-md text-[15px] font-light leading-relaxed text-ivory/80">
                                Our primary market — Delhi NCR first, with selected high-growth corridors assessed on real demand, not sentiment.
                            </p>
                        </div>
                    </Link>
                </Reveal>
                <Reveal delay={0.12} className="md:col-span-5">
                    <Link to="/opportunities?market=dubai" data-testid="market-card-dubai" className="group relative block">
                        <div className="img-frame aspect-[4/3] bg-secondary">
                            <img
                                src={unsplashSrc(IMG.dubai, 1200)}
                                srcSet={unsplashSrcset(IMG.dubai)}
                                sizes="(max-width: 768px) 100vw, 42vw"
                                alt="Dubai residential skyline"
                                loading="lazy"
                                decoding="async"
                            />
                        </div>
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/10 to-transparent" />
                        <span className="absolute right-6 top-6 flex h-10 w-10 items-center justify-center border border-ivory/50 text-ivory opacity-0 transition-opacity duration-500 group-hover:opacity-100">
                            <ArrowUpRight className="h-4 w-4" />
                        </span>
                        <div className="absolute bottom-0 left-0 p-8 md:p-10">
                            <p className="text-[10px] font-medium uppercase tracking-[0.3em] text-gold">International Market</p>
                            <h3 className="mt-2 font-serif text-3xl font-light tracking-tight text-ivory md:text-4xl">Dubai</h3>
                            <p className="mt-2 max-w-md text-[15px] font-light leading-relaxed text-ivory/80">
                                An active international market, accessed through developers and real-estate partners we work with directly.
                            </p>
                        </div>
                    </Link>
                </Reveal>
            </div>
            <Reveal className="mt-8 flex flex-wrap items-baseline gap-x-8 gap-y-3 border border-ink/10 bg-white/60 px-8 py-7" data-testid="market-global-note">
                <span className="text-[11px] font-medium uppercase tracking-[0.3em] text-gold">Global</span>
                <p className="text-[15px] font-light leading-relaxed text-ink/65">
                    Selected international markets may be introduced as the LUXATI network develops.
                </p>
            </Reveal>
        </section>

        {/* 04 — How LUXATI Works (dark) */}
        <section data-testid="process-section" className="grain-overlay bg-charcoal text-ivory">
            <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
                <Chapter number="04" label="How LUXATI Works" dark />
                <Reveal className="mt-12 max-w-2xl">
                    <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl">
                        A Considered <span className="italic text-gold">Process.</span>
                    </h2>
                </Reveal>
                <div className="mt-20 grid gap-12 md:grid-cols-4 md:gap-8">
                    {STEPS.map((s, i) => (
                        <Reveal key={s.n} delay={i * 0.12} className="border-t border-ivory/15 pt-8 transition-colors duration-700 hover:border-gold/60">
                            <p className="font-serif text-5xl font-light italic text-gold/80">{s.n}</p>
                            <h3 className="mt-6 font-serif text-2xl font-medium tracking-tight">{s.t}</h3>
                            <p className="mt-4 text-[15px] font-light leading-relaxed text-ivory/65">{s.c}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* 05 — Why LUXATI */}
        <section data-testid="why-section" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
            <Chapter number="05" label="Why LUXATI" />
            <div className="mt-12 grid gap-16 md:grid-cols-12">
                <Reveal className="md:col-span-6">
                    <h2 className="font-serif text-4xl font-light leading-[1.05] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                        Fewer Properties.<br />
                        <span className="italic text-gold">Better Decisions.</span>
                    </h2>
                    <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/70">
                        LUXATI is designed around relevance rather than volume — so that every opportunity
                        you see has already earned its place.
                    </p>
                </Reveal>
                <div className="md:col-span-5 md:col-start-8">
                    {PRINCIPLES.map((p, i) => (
                        <Reveal key={p.t} delay={i * 0.12} className="group border-b border-ink/10 py-8 first:pt-0">
                            <div className="flex items-baseline justify-between">
                                <h3 className="font-serif text-2xl font-medium tracking-tight text-ink md:text-3xl">{p.t}</h3>
                                <span className="font-serif text-sm italic text-gold">{String(i + 1).padStart(2, "0")}</span>
                            </div>
                            <p className="mt-3 max-w-sm text-[15px] font-light leading-relaxed text-ink/70">{p.c}</p>
                        </Reveal>
                    ))}
                </div>
            </div>
        </section>

        {/* 06 — For Developers & Partners */}
        <section data-testid="partners-section" className="border-t border-ink/10 bg-white/50">
            <div className="mx-auto grid max-w-[1400px] gap-16 px-6 py-28 md:grid-cols-12 md:px-12 md:py-36">
                <div className="md:col-span-5">
                    <Chapter number="06" label="For Developers & Partners" />
                    <Reveal className="mt-10">
                        <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl">
                            Built for Better Real Estate <span className="italic text-gold">Partnerships.</span>
                        </h2>
                        <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/70">
                            We work with developers and their sales teams to place selected projects in
                            front of qualified buyers — and with real-estate partners building structured
                            distribution.
                        </p>
                        <div className="img-frame mt-12 aspect-[4/3] bg-secondary">
                            <img src={unsplashSrc(IMG.interiorAlt, 960)} srcSet={unsplashSrcset(IMG.interiorAlt)} sizes="(max-width: 768px) 100vw, 42vw" alt="Luxury interior" loading="lazy" decoding="async" />
                        </div>
                    </Reveal>
                </div>
                <Reveal delay={0.15} className="md:col-span-6 md:col-start-7 md:pt-24">
                    <Overline className="mb-8">Partnership Enquiry</Overline>
                    <PartnerForm testid="home-partner-form" />
                </Reveal>
            </div>
        </section>

        {/* 07 — For Buyers & Investors */}
        <section data-testid="investors-section" className="mx-auto grid max-w-[1400px] gap-16 px-6 py-28 md:grid-cols-12 md:px-12 md:py-36">
            <Reveal className="md:col-span-6 md:pt-24">
                <Overline className="mb-8">Buyer & Investor Enquiry</Overline>
                <BuyerForm testid="home-buyer-form" />
            </Reveal>
            <div className="md:col-span-5 md:col-start-8">
                <Chapter number="07" label="For Buyers & Investors" />
                <Reveal className="mt-10">
                    <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl">
                        Tell Us What You're <span className="italic text-gold">Looking For.</span>
                    </h2>
                    <p className="mt-8 max-w-md text-base font-light leading-relaxed text-ink/70">
                        Tell us the objective, the budget and the timeline. We respond with a shortlist —
                        and the reasoning behind it.
                    </p>
                    <div className="img-frame mt-12 aspect-[4/3] bg-secondary">
                        <img src={unsplashSrc(IMG.interior, 960)} srcSet={unsplashSrcset(IMG.interior)} sizes="(max-width: 768px) 100vw, 42vw" alt="Luxury living room interior" loading="lazy" decoding="async" />
                    </div>
                </Reveal>
            </div>
        </section>

        {/* 08 — About */}
        <section data-testid="about-section" className="grain-overlay bg-charcoal text-ivory">
            <div className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-40">
                <Chapter number="08" label="About LUXATI" dark />
                <div className="mt-12 grid gap-12 md:grid-cols-12">
                    <Reveal className="md:col-span-7">
                        <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight sm:text-5xl lg:text-6xl">
                            A More Considered Approach to <span className="italic text-gold">Real Estate.</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15} className="md:col-span-4 md:col-start-9">
                        <p className="text-base font-light leading-relaxed text-ivory/70">
                            LUXATI was created around a simple idea: property decisions should begin with
                            understanding, not inventory.
                        </p>
                        <p className="mt-6 text-base font-light leading-relaxed text-ivory/70">
                            We work across buyers, investors, developers and partners — connecting the
                            people evaluating opportunities with the people who have them.
                        </p>
                        <Link
                            to="/about"
                            data-testid="about-read-more"
                            className="group mt-10 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-gold"
                        >
                            More About LUXATI
                            <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                        </Link>
                    </Reveal>
                </div>
            </div>
        </section>

        {/* 09 — Technology */}
        <section data-testid="technology-section" className="mx-auto max-w-[1400px] px-6 py-28 md:px-12 md:py-36">
            <Chapter number="09" label="Technology" />
            <div className="mt-12 grid gap-12 md:grid-cols-12">
                <Reveal className="md:col-span-6">
                    <h2 className="font-serif text-4xl font-light leading-[1.08] tracking-tight text-ink sm:text-5xl">
                        Real Estate, <span className="italic text-gold">Organised Better.</span>
                    </h2>
                </Reveal>
                <Reveal delay={0.15} className="md:col-span-5 md:col-start-8">
                    <p className="text-base font-light leading-relaxed text-ink/70">
                        LUXATI is developing a more structured way to organise property information, buyer
                        requirements and real estate relationships — helping create a clearer advisory
                        experience over time.
                    </p>
                    <p className="mt-6 text-base font-light leading-relaxed text-ink/70">
                        The platform behind this work is also available to developers, brokerages and
                        advisories — as their own white-label environment.
                    </p>
                    <Link
                        to="/technology"
                        data-testid="home-technology-link"
                        className="group mt-8 inline-flex items-center gap-3 text-[11px] font-medium uppercase tracking-[0.22em] text-[#8a6a1c]"
                    >
                        Explore LUXATI Technology
                        <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
                    </Link>
                </Reveal>
            </div>
        </section>

        {/* Final CTA */}
        <section data-testid="final-cta-section" className="grain-overlay relative overflow-hidden bg-charcoal text-ivory">
            <div className="absolute inset-x-0 top-0 h-px bg-gold/40" />
            <div className="mx-auto max-w-[1400px] px-6 py-32 text-center md:px-12 md:py-44">
                <Reveal>
                    <Overline>Begin The Conversation</Overline>
                    <h2 className="mx-auto mt-8 max-w-3xl font-serif text-4xl font-light leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
                        Looking for the right <span className="italic text-gold">opportunity?</span>
                    </h2>
                    <p className="mx-auto mt-8 max-w-xl text-base font-light leading-relaxed text-ivory/70">
                        Tell us what you're looking for. We'll start with the opportunities that make sense for you.
                    </p>
                    <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                        <Link
                            to="/contact"
                            data-testid="final-cta-speak"
                            className="inline-flex items-center gap-3 bg-ivory px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ink transition-colors duration-500 hover:bg-gold"
                        >
                            Speak With LUXATI
                        </Link>
                        <Link
                            to="/opportunities"
                            data-testid="final-cta-explore"
                            className="inline-flex items-center gap-3 border border-ivory/40 px-8 py-4 text-[11px] font-medium uppercase tracking-[0.22em] text-ivory transition-colors duration-500 hover:border-gold hover:text-gold"
                        >
                            Explore Opportunities
                        </Link>
                    </div>
                </Reveal>
            </div>
        </section>
    </main>
);

export default Home;
